import { useState, useEffect, useCallback, useMemo } from 'react';

interface ImageCacheOptions {
  maxCacheSize?: number;  // Maximum number of images to cache in memory
  preloadAdjacent?: boolean; // Whether to preload adjacent images in a gallery
  cacheExpiry?: number; // Cache expiry time in milliseconds
  skipCorsCheck?: boolean; // Whether to skip CORS checks for cross-origin images
}

/**
 * Custom hook for efficient image caching and preloading
 * Implements:
 * 1. In-memory cache for faster repeat views
 * 2. Browser cache control via headers
 * 3. Preloading of adjacent images in galleries
 * 4. Automatic cache cleanup
 */
export const useImageCache = (options: ImageCacheOptions = {}) => {
  const {
    maxCacheSize = 50,
    preloadAdjacent = true,
    cacheExpiry = 24 * 60 * 60 * 1000, // 24 hours by default
    skipCorsCheck = false, // Default to respecting CORS
  } = options;

  // In-memory cache of loaded images
  const [imageCache, setImageCache] = useState<Record<string, { 
    blob: Blob, 
    timestamp: number,
    objectUrl: string 
  }>>({});
  
  // Cache statistics for monitoring
  const [cacheStats, setCacheStats] = useState({
    hits: 0,
    misses: 0,
    size: 0
  });

  // Clean up expired cache entries
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setImageCache(prevCache => {
        const newCache = { ...prevCache };
        let hasChanges = false;
        
        // Remove expired items
        Object.entries(newCache).forEach(([url, entry]) => {
          if (now - entry.timestamp > cacheExpiry) {
            URL.revokeObjectURL(entry.objectUrl);
            delete newCache[url];
            hasChanges = true;
          }
        });
        
        return hasChanges ? newCache : prevCache;
      });
    }, 60 * 60 * 1000); // Check every hour
    
    return () => clearInterval(cleanupInterval);
  }, [cacheExpiry]);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imageCache).forEach(entry => {
        URL.revokeObjectURL(entry.objectUrl);
      });
    };
  }, []);

  // Load and cache an image
  const loadImage = useCallback(async (url: string): Promise<string> => {
    // Check if image is already in cache
    if (imageCache[url]) {
      setCacheStats(prev => ({ ...prev, hits: prev.hits + 1 }));
      return imageCache[url].objectUrl;
    }
    
    // Skip fetching for cross-origin URLs unless explicitly allowed
    if (!skipCorsCheck) {
      try {
        // Check if URL is cross-origin
        const urlObj = new URL(url, window.location.origin);
        const isCrossOrigin = urlObj.origin !== window.location.origin;
        
        if (isCrossOrigin) {
          // For cross-origin URLs, don't attempt to fetch - let the img tag handle it
          return url;
        }
      } catch (e) {
        // If URL parsing fails, just return the original URL
        return url;
      }
    }
    
    try {
      // Fetch the image with cache-control headers
      const response = await fetch(url, {
        cache: 'force-cache', // Use browser's cache if available
        headers: {
          'Cache-Control': 'max-age=86400' // 24 hours
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }
      
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      // Update cache
      setImageCache(prev => {
        // If cache is full, remove oldest entry
        const entries = Object.entries(prev);
        if (entries.length >= maxCacheSize) {
          const oldestEntry = entries.sort((a, b) => a[1].timestamp - b[1].timestamp)[0];
          if (oldestEntry) {
            URL.revokeObjectURL(oldestEntry[1].objectUrl);
            const { [oldestEntry[0]]: _, ...rest } = prev;
            return {
              ...rest,
              [url]: { blob, timestamp: Date.now(), objectUrl }
            };
          }
        }
        
        return {
          ...prev,
          [url]: { blob, timestamp: Date.now(), objectUrl }
        };
      });
      
      setCacheStats(prev => ({ 
        ...prev, 
        misses: prev.misses + 1,
        size: prev.size + 1
      }));
      
      return objectUrl;
    } catch (error) {
      // More specific error logging
      if (process.env.NODE_ENV === 'development') {
        console.debug('Skipping cache for image:', url);
      }
      return url; // Fall back to original URL on error
    }
  }, [imageCache, maxCacheSize, skipCorsCheck]);
  
  // Cache an already loaded image from an img element
  const cacheLoadedImage = useCallback((url: string, imgElement: HTMLImageElement) => {
    // Skip if already cached
    if (imageCache[url]) {
      return imageCache[url].objectUrl;
    }
    
    try {
      // Create a canvas to get image data
      const canvas = document.createElement('canvas');
      canvas.width = imgElement.naturalWidth;
      canvas.height = imgElement.naturalHeight;
      
      // Draw the image to canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }
      
      // Try to draw the image to canvas
      try {
        ctx.drawImage(imgElement, 0, 0);
      } catch (e) {
        // This can fail for cross-origin images without CORS headers
        // Just return the original URL in this case
        return url;
      }
      
      // Convert to blob
      canvas.toBlob(blob => {
        if (blob) {
          // Create an object URL and store in cache
          const objectUrl = URL.createObjectURL(blob);
          
          setImageCache(prev => {
            // If cache is full, remove oldest entry
            const entries = Object.entries(prev);
            if (entries.length >= maxCacheSize) {
              const oldestEntry = entries.sort((a, b) => a[1].timestamp - b[1].timestamp)[0];
              if (oldestEntry) {
                URL.revokeObjectURL(oldestEntry[1].objectUrl);
                const { [oldestEntry[0]]: _, ...rest } = prev;
                return {
                  ...rest,
                  [url]: { blob, timestamp: Date.now(), objectUrl }
                };
              }
            }
            
            return {
              ...prev,
              [url]: { blob, timestamp: Date.now(), objectUrl }
            };
          });
          
          setCacheStats(prev => ({
            ...prev,
            size: prev.size + 1
          }));
        }
      }, 'image/webp', 0.9); // Use webp for better compression
      
      return url; // Return original URL while caching happens asynchronously
    } catch (error) {
      // Silently fail and return original URL
      return url;
    }
  }, [imageCache, maxCacheSize]);

  // Preload images without displaying them
  const preloadImages = useCallback((urls: string[]) => {
    urls.forEach(url => {
      // Only preload if not already cached
      if (!imageCache[url]) {
        loadImage(url).catch(err => {
          console.warn(`Failed to preload image ${url}:`, err);
        });
      }
    });
  }, [imageCache, loadImage]);

  // Preload adjacent images in a gallery
  const preloadAdjacentImages = useCallback((currentIndex: number, allImageUrls: string[]) => {
    if (!preloadAdjacent || allImageUrls.length <= 1) return;
    
    const imagesToPreload: string[] = [];
    
    // Preload next image
    if (currentIndex < allImageUrls.length - 1) {
      imagesToPreload.push(allImageUrls[currentIndex + 1]);
    }
    
    // Preload previous image
    if (currentIndex > 0) {
      imagesToPreload.push(allImageUrls[currentIndex - 1]);
    }
    
    preloadImages(imagesToPreload);
  }, [preloadAdjacent, preloadImages]);

  // Clear specific images from cache
  const clearFromCache = useCallback((urls: string[]) => {
    setImageCache(prev => {
      const newCache = { ...prev };
      let hasChanges = false;
      
      urls.forEach(url => {
        if (newCache[url]) {
          URL.revokeObjectURL(newCache[url].objectUrl);
          delete newCache[url];
          hasChanges = true;
        }
      });
      
      return hasChanges ? newCache : prev;
    });
    
    setCacheStats(prev => ({
      ...prev,
      size: Math.max(0, prev.size - urls.length)
    }));
  }, []);

  // Clear entire cache
  const clearCache = useCallback(() => {
    Object.values(imageCache).forEach(entry => {
      URL.revokeObjectURL(entry.objectUrl);
    });
    
    setImageCache({});
    setCacheStats(prev => ({ ...prev, size: 0 }));
  }, [imageCache]);

  // Memoize cache stats to prevent unnecessary re-renders
  const memoizedCacheStats = useMemo(() => cacheStats, [cacheStats]);

  return {
    loadImage,
    preloadImages,
    preloadAdjacentImages,
    clearFromCache,
    clearCache,
    cacheLoadedImage,
    cacheStats: memoizedCacheStats,
    isCached: useCallback((url: string) => !!imageCache[url], [imageCache])
  };
};
